'use client';

import { useRouter } from 'next/navigation';
import type React from 'react';
import { useState } from 'react';
import ActionButton from '../../../../components/form/ActionButton/ActionButton';
import FaIcon, { faTriangleExclamation } from '../../../../components/icon/FaIcon/FaIcon';
import ImageView from '../../../../components/ui/ImageView/ImageView';
import ModalBase from '../../../../components/ui/ModalBase/ModalBase';
import UnRegisteredAddressAlert from '../../../../components/ui/UnRegisteredAddressAlert/UnRegisteredAddressAlert';
import { swal } from '../../../../lib/sweetalert';
import utilsStyles from '../../../../styles/modules/utils.module.scss';
import { pickErrorMessages } from '../../../../utils/functions';
import type { PostRepostCampaignAchieveParams } from '../../api/v2/postRepostCampaignAchieve';
import type { PrizeConvertedGoodsItem, RepostCampaignDetail } from '../../types';
import RepostPrizeList from '../RepostPrizeList.tsx/RepostPrizeList';
import styles from './RepostCampaignSelectablePreview.module.scss';

export type RepostCampaignSelectablePreviewProps = {
  campaignDetail: RepostCampaignDetail;
  code: string;
  postRepostCampaignAchieve: (params: PostRepostCampaignAchieveParams) => Promise<void>;
  handleGoMainAddress: (e: React.MouseEvent) => void;
};

function RepostCampaignSelectablePreview({
  campaignDetail,
  code,
  postRepostCampaignAchieve,
  handleGoMainAddress,
}: RepostCampaignSelectablePreviewProps) {
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmPrize, setConfirmPrize] = useState<{ id: number; name: string; thumbnailUrl: string }>({
    id: 0,
    name: '',
    thumbnailUrl: '',
  });
  const openConfirmReceiveModal = (goods: PrizeConvertedGoodsItem) => {
    setConfirmPrize({ id: goods.id, name: goods.name, thumbnailUrl: goods.imageUrl });
    setIsModalOpen(true);
  };

  const closeConfirmReceiveModal = () => {
    setIsModalOpen(false);
  };

  const handleReceiveCampaignPrize = async (goodsId: number) => {
    try {
      if (isLoading) return;
      setIsLoading(true);
      await postRepostCampaignAchieve({
        code: code,
        selectedGoodsId: goodsId,
      });
      swal.alert({ text: '景品を受け取りました' });
      router.push('/account/collection');
    } catch (error) {
      await swal.messages({ messages: pickErrorMessages(error) });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.componentStyle}>
      <p className={`${utilsStyles.textLarge} ${utilsStyles.errorColor} ${utilsStyles.textBold}`}>
        <FaIcon icon={faTriangleExclamation} />
        &nbsp; 希望する景品を選択してください
      </p>
      <div className={utilsStyles.mtSmall}>
        <RepostPrizeList items={[campaignDetail.prize]} onClickPrizeItem={openConfirmReceiveModal} />
      </div>
      {!campaignDetail.setAddress && (
        <div className={utilsStyles.mtSmall}>
          <UnRegisteredAddressAlert handleGoMainAddress={handleGoMainAddress} />
        </div>
      )}
      <ModalBase isOpen={isModalOpen} onClose={closeConfirmReceiveModal}>
        <div className={styles.confirmSelectModalContainer}>
          <p className={`${utilsStyles.textBold} ${utilsStyles.textXLarge} ${utilsStyles.textCenter}`}>
            獲得予定の景品
          </p>
          <p className={`${styles.repostWrapper} ${utilsStyles.textLarge} ${utilsStyles.mtSmall}`}>
            リポストキャンペーン
          </p>
          <p className={`${utilsStyles.textBold} ${utilsStyles.textMedium} ${utilsStyles.mtXSmall}`}>
            {confirmPrize.name}
          </p>
          <div className={`${styles.confirmSelectImageContainer} ${utilsStyles.mtXSmall}`}>
            <ImageView src={confirmPrize.thumbnailUrl} alt={confirmPrize.name} objectFit='contain' />
          </div>
          <p
            className={`${utilsStyles.textLarge} ${utilsStyles.errorColor} ${utilsStyles.textBold} ${utilsStyles.mtXSmall}`}
          >
            <FaIcon icon={faTriangleExclamation} />
            &nbsp; 景品獲得後の希望景品変更はいたしかねます
          </p>
          <div className={`${styles.confirmSelectModalButtonsContainer} ${utilsStyles.mtXSmall}`}>
            <ActionButton color='warmgray' onClick={closeConfirmReceiveModal}>
              キャンセル
            </ActionButton>
            <ActionButton
              color='primary'
              onClick={() => handleReceiveCampaignPrize(confirmPrize.id)}
              disabled={!campaignDetail.setAddress}
            >
              OK
            </ActionButton>
          </div>
        </div>
      </ModalBase>
    </div>
  );
}

export default RepostCampaignSelectablePreview;
